import React, { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Star, TrendingUp, TrendingDown, Award } from 'lucide-react';

const TIER_ORDER: Record<string, number> = { R: 0, D: 1, C: 2, B: 3, A: 4, S: 5 };

// ── Tier configuration ─────────────────────────────────────────
const TIER_META: Record<string, {
  label: string;
  color: string;
  glow: string;
  gradient: string;
  bg: string;
  icon: string;
}> = {
  A: {
    label: 'CARTEIRA A',
    color: '#22d3ee',
    glow: 'rgba(34,211,238,0.4)',
    gradient: 'linear-gradient(135deg, rgba(34,211,238,0.15), rgba(34,211,238,0.04))',
    bg: 'rgba(34,211,238,0.08)',
    icon: '🏆',
  },
  B: {
    label: 'CARTEIRA B',
    color: '#f9ca36',
    glow: 'rgba(249,202,54,0.4)',
    gradient: 'linear-gradient(135deg, rgba(249,202,54,0.15), rgba(249,202,54,0.04))',
    bg: 'rgba(249,202,54,0.08)',
    icon: '⚡',
  },
  C: {
    label: 'CARTEIRA C',
    color: '#98c136',
    glow: 'rgba(152,193,54,0.4)',
    gradient: 'linear-gradient(135deg, rgba(152,193,54,0.15), rgba(152,193,54,0.04))',
    bg: 'rgba(152,193,54,0.08)',
    icon: '🎯',
  },
  D: {
    label: 'CARTEIRA D',
    color: '#1b80c0',
    glow: 'rgba(27,128,192,0.4)',
    gradient: 'linear-gradient(135deg, rgba(27,128,192,0.15), rgba(27,128,192,0.04))',
    bg: 'rgba(27,128,192,0.08)',
    icon: '🚀',
  },
};

// ── Confetti Particle System ───────────────────────────────────
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  size: number;
  shape: 'rect' | 'circle' | 'triangle';
  opacity: number;
  life: number;
  maxLife: number;
}

function createParticle(canvasW: number, tierColor: string): Particle {
  const colors = [
    tierColor,
    '#ffffff',
    '#f9ca36',
    '#98c136',
    '#22d3ee',
    '#fb923c',
    '#a78bfa',
  ];
  const shapes: Particle['shape'][] = ['rect', 'circle', 'triangle'];
  const maxLife = 180 + Math.random() * 120;

  return {
    x: Math.random() * canvasW,
    y: -10,
    vx: (Math.random() - 0.5) * 4,
    vy: 2 + Math.random() * 4,
    color: colors[Math.floor(Math.random() * colors.length)],
    rotation: Math.random() * Math.PI * 2,
    rotationSpeed: (Math.random() - 0.5) * 0.2,
    size: 4 + Math.random() * 8,
    shape: shapes[Math.floor(Math.random() * shapes.length)],
    opacity: 1,
    life: 0,
    maxLife,
  };
}

function drawParticle(ctx: CanvasRenderingContext2D, p: Particle) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rotation);
  ctx.globalAlpha = p.opacity * (1 - p.life / p.maxLife);
  ctx.fillStyle = p.color;

  if (p.shape === 'rect') {
    ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
  } else if (p.shape === 'circle') {
    ctx.beginPath();
    ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.moveTo(0, -p.size / 2);
    ctx.lineTo(p.size / 2, p.size / 2);
    ctx.lineTo(-p.size / 2, p.size / 2);
    ctx.closePath();
    ctx.fill();
  }

  ctx.restore();
}

// ── Component ──────────────────────────────────────────────────
interface PromotionModalProps {
  isOpen: boolean;
  newTier: string;
  prevTier: string;
  pilotName: string;
  onClose: () => void;
}

const PromotionModal: React.FC<PromotionModalProps> = ({
  isOpen,
  newTier,
  prevTier,
  pilotName,
  onClose,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>([]);
  const spawnCountRef = useRef(0);

  const meta = TIER_META[newTier] ?? TIER_META['D'];

  // ── Particle animation ───────────────────────────────────────
  const startParticles = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    particlesRef.current = [];
    spawnCountRef.current = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Spawn burst at beginning
      if (spawnCountRef.current < 200) {
        for (let i = 0; i < 5; i++) {
          particlesRef.current.push(createParticle(canvas.width, meta.color));
        }
        spawnCountRef.current += 5;
      }

      // Continuous drizzle after burst
      if (spawnCountRef.current >= 200 && Math.random() < 0.3) {
        particlesRef.current.push(createParticle(canvas.width, meta.color));
      }

      // Update & draw
      particlesRef.current = particlesRef.current.filter(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05; // gravity
        p.vx *= 0.99;  // air drag
        p.rotation += p.rotationSpeed;
        p.life++;
        drawParticle(ctx, p);
        return p.life < p.maxLife && p.y < canvas.height + 20;
      });

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
  }, [meta.color]);

  const stopParticles = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    particlesRef.current = [];
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => startParticles(), 100);
    } else {
      stopParticles();
    }
    return () => stopParticles();
  }, [isOpen, startParticles, stopParticles]);

  // ── Handle resize ────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // ── Close on Escape ──────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isDemotion = (TIER_ORDER[newTier] ?? 0) < (TIER_ORDER[prevTier] ?? 0);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('promotion_modal.aria_label', 'Mudança de Carteira')}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
        style={{ animation: 'fadeIn 0.3s ease-out' }}
      />

      {/* Canvas confetti layer */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none z-10"
      />

      {/* Modal panel */}
      <div
        className="relative z-20 w-full max-w-md rounded-2xl overflow-hidden border"
        style={{
          background: 'linear-gradient(160deg, #0d1017 0%, #131720 100%)',
          borderColor: `${meta.color}40`,
          boxShadow: `0 0 60px ${meta.glow}, 0 0 120px ${meta.glow}50, 0 20px 60px rgba(0,0,0,0.6)`,
          animation: 'modalIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Top accent bar */}
        <div
          className="h-1 w-full"
          style={{ background: `linear-gradient(90deg, transparent, ${meta.color}, transparent)` }}
        />

        {/* Close button */}
        <button
          onClick={onClose}
          id="promotion-modal-close"
          className="absolute top-4 right-4 p-2 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-all z-10"
          aria-label={t('common.close')}
        >
          <X size={16} />
        </button>

        <div className="p-8 pb-10 text-center">
          {/* Animated icon burst */}
          <div
            className="relative mx-auto mb-6 w-28 h-28 flex items-center justify-center"
            style={{ animation: 'pulse-glow 2s ease-in-out infinite' }}
          >
            {/* Outer ring */}
            <div
              className="absolute inset-0 rounded-full border-2 opacity-30"
              style={{
                borderColor: meta.color,
                animation: 'ring-expand 2s ease-in-out infinite',
              }}
            />
            {/* Middle ring */}
            <div
              className="absolute inset-2 rounded-full border"
              style={{
                borderColor: `${meta.color}60`,
                animation: 'ring-expand 2s 0.3s ease-in-out infinite',
              }}
            />

            {/* Center circle */}
            <div
              className="relative w-20 h-20 rounded-full flex items-center justify-center text-4xl font-black italic font-display"
              style={{
                background: meta.gradient,
                border: `2px solid ${meta.color}60`,
                color: meta.color,
                boxShadow: `0 0 30px ${meta.glow}`,
              }}
            >
              {newTier}
            </div>
          </div>

          {/* Title */}
          <div
            className="font-label text-xs font-bold uppercase tracking-[0.3em] mb-2"
            style={{ color: `${meta.color}80` }}
          >
            {isDemotion
              ? t('promotion_modal.demotion_header', 'ATUALIZAÇÃO DE CARTEIRA')
              : t('promotion_modal.congratulations')}
          </div>
          <h2
            className="text-3xl font-display font-black italic uppercase tracking-tight mb-1"
            style={{ color: meta.color, textShadow: `0 0 20px ${meta.glow}` }}
          >
            {isDemotion
              ? t('promotion_modal.demotion_title', 'REBAIXAMENTO DE CARTEIRA')
              : t('promotion_modal.title')}
          </h2>
          <p className="font-label text-sm text-white/50 mb-6">
            {isDemotion
              ? t('promotion_modal.demotion_subtitle', '{{name}}, sua carteira na plataforma foi ajustada.', { name: pilotName })
              : t('promotion_modal.subtitle', { name: pilotName })}
          </p>

          {/* Tier upgrade visualization */}
          <div
            className="flex items-center justify-center gap-4 p-4 rounded-xl mb-6"
            style={{ background: meta.bg }}
          >
            {/* Old tier */}
            <div className="text-center">
              <span className="font-label text-[9px] text-white/30 uppercase tracking-widest block mb-1">
                {t('promotion_modal.from')}
              </span>
              <span
                className="w-10 h-10 rounded-lg flex items-center justify-center text-lg font-black italic font-display border border-white/10"
                style={{ color: '#b5b5b5', background: 'rgba(255,255,255,0.03)' }}
              >
                {prevTier}
              </span>
            </div>

            {/* Arrow */}
            <div className="flex items-center gap-1">
              {isDemotion ? (
                <TrendingDown size={24} style={{ color: meta.color }} />
              ) : (
                <TrendingUp size={24} style={{ color: meta.color }} />
              )}
            </div>

            {/* New tier */}
            <div className="text-center">
              <span className="font-label text-[9px] text-white/30 uppercase tracking-widest block mb-1">
                {t('promotion_modal.to')}
              </span>
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-black italic font-display border-2"
                style={{
                  color: meta.color,
                  background: meta.gradient,
                  borderColor: `${meta.color}50`,
                  boxShadow: `0 0 20px ${meta.glow}`,
                  animation: 'tierPulse 1.5s ease-in-out infinite',
                }}
              >
                {newTier}
              </div>
            </div>
          </div>

          {/* Description */}
          <p
            className="text-xs text-white/50 font-label leading-relaxed mb-8"
            style={{ letterSpacing: '0.05em' }}
          >
            {isDemotion
              ? t('promotion_modal.demotion_description', 'Sua carteira de piloto foi reajustada para Carteira {{tier}}. Continue competindo para evoluir novamente.', { tier: newTier })
              : t('promotion_modal.description', { tier: newTier })}
          </p>

          {/* CTA */}
          <button
            id="promotion-modal-confirm"
            onClick={onClose}
            className="w-full py-3.5 rounded-xl font-label text-sm font-black uppercase tracking-widest transition-all duration-200 active:scale-95 hover:scale-[1.02]"
            style={{
              background: `linear-gradient(135deg, ${meta.color}CC, ${meta.color}99)`,
              color: '#0a0b0f',
              boxShadow: `0 4px 20px ${meta.glow}`,
            }}
          >
            <span className="flex items-center justify-center gap-2">
              <Award size={16} />
              {t('promotion_modal.cta')}
            </span>
          </button>

          {/* Stars decoration */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={10}
                fill={meta.color}
                style={{
                  color: meta.color,
                  opacity: 0.6,
                  animation: `twinkle ${0.5 + i * 0.2}s ease-in-out infinite alternate`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Bottom accent bar */}
        <div
          className="h-px w-full"
          style={{ background: `linear-gradient(90deg, transparent, ${meta.color}40, transparent)` }}
        />
      </div>

      {/* Keyframe animations injected once */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.7) translateY(30px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes ring-expand {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.1); opacity: 0.1; }
        }
        @keyframes pulse-glow {
          0%, 100% { filter: drop-shadow(0 0 10px currentColor); }
          50% { filter: drop-shadow(0 0 25px currentColor); }
        }
        @keyframes tierPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes twinkle {
          from { opacity: 0.3; transform: scale(0.8); }
          to { opacity: 0.9; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

export default PromotionModal;
